import {
  type ResolvedDateTimeFormat,
  type TimePrecision,
} from "@/shared/datetime";
import { activeDateTimeFormat } from "@/shared/datetime/formatters";
import { timeZoneName } from "@/shared/datetime/timeZones";
import { type FractionDigits } from "@/shared/time";

import { MS_PER_DAY, wallClockMs } from "./boardCalendar";
import { type InteractionTimeUnit } from "./interactionTypes";
import { NANOSECONDS_PER_UNIT } from "./interactionValueCodec";

/*
 * How a board writes a time for a person to read: the date and time the
 * board's zone's clocks show, in the viewer's date and time format (Account →
 * Preferences), then the zone's name in parentheses.
 *
 * - A midnight is its date alone: "January 4, 2026 (UTC)".
 * - Any other time has hours and minutes, and seconds only where they are not
 *   zero, with the digits below the second the time needs:
 *   "January 4, 2026 at 2:05 PM (UTC)",
 *   "January 4, 2026 at 2:05:30.25 PM (UTC)".
 * - The ISO format writes the date as YYYY-MM-DD and the time on the
 *   viewer's clock: "2026-01-04 (UTC)" for a midnight,
 *   "2026-01-04 14:05:30.25 (UTC)" on a 24-hour clock and
 *   "2026-01-04 2:05:30.25 PM (UTC)" on a 12-hour one.
 *
 * A time axis labels its ticks more briefly (`boardTickText`). Text a program
 * reads, such as a CSV or JSON export and the dry run the chat assistant
 * reads, stays ISO 8601 with the zone's offset.
 *
 * The viewer's format comes from the app's formatting registry, which holds
 * the app's default format until the viewer's preference loads, and for a
 * viewer who never chose one.
 */

const NS_PER_MS = 1_000_000n;

/** The viewer's format, reading instants in UTC. `wallClockMs` writes a
 *  zone's clock reading as if it were a UTC time, so this format writes the
 *  time the zone's clocks show. */
function wallClockFormat(): ResolvedDateTimeFormat {
  return activeDateTimeFormat("UTC");
}

/** How much of a wall-clock time `boardWallClockText` writes. */
export interface BoardWallClockForm {
  /** Digits below the second. Default: the fewest that write the time
   *  exactly. */
  fractionDigits?: FractionDigits;
  /** Write a midnight's time as well as its date, as the end of a span that
   *  is not whole days needs. Default: a midnight is its date alone. */
  timeAtMidnight?: boolean;
}

/** How much of an instant `boardInstantText` writes. */
export interface BoardInstantForm extends BoardWallClockForm {
  /** Leave out the zone's name, where something beside the time names it
   *  once for many times, as an axis's title does. Default: the time ends
   *  with the zone's name. */
  withoutZone?: boolean;
}

/**
 * An instant `ticks` counts of `unit` after the Unix epoch, as a board writes
 * it in `timeZone`: "January 4, 2026 at 2:05 PM (UTC)", or
 * "January 4, 2026 (UTC)" for a midnight. An instant whose time in the zone
 * falls outside the range a JavaScript `Date` covers is written as its count
 * and unit.
 */
export function boardInstantText(
  ticks: bigint,
  unit: InteractionTimeUnit,
  timeZone: string,
  form: BoardInstantForm = {},
): string {
  const nanoseconds = ticks * NANOSECONDS_PER_UNIT[unit];
  const nsBelowMs = ((nanoseconds % NS_PER_MS) + NS_PER_MS) % NS_PER_MS;
  const ms = Number((nanoseconds - nsBelowMs) / NS_PER_MS);
  const countText = `${ticks.toString()} ${unit} since Unix epoch`;
  if (!Number.isFinite(new Date(ms).valueOf())) {
    return countText;
  }
  const wallMs = wallClockMs(timeZone, ms);
  // An instant near the end of the range a `Date` covers can read, on a
  // zone's clocks, as a time past it.
  if (!Number.isFinite(new Date(wallMs).valueOf())) {
    return countText;
  }
  const text = boardWallClockText(wallMs, nsBelowMs, form);
  return form.withoutZone ? text : `${text} ${boardZoneText(timeZone)}`;
}

/**
 * A wall-clock time, as `wallClockMs` writes it, the way `boardInstantText`
 * writes it before the zone's name: "January 4, 2026 at 2:05 PM", or
 * "January 4, 2026" for a midnight. `nsBelowMs` adds the nanoseconds below
 * the millisecond.
 */
export function boardWallClockText(
  wallMs: number,
  nsBelowMs: bigint = 0n,
  form: BoardWallClockForm = {},
): string {
  const time = new Date(wallMs);
  const format = wallClockFormat();
  if (wallMs % MS_PER_DAY === 0 && nsBelowMs === 0n && !form.timeAtMidnight) {
    return dateText(time, format);
  }
  const fraction = fractionText(time, nsBelowMs, form.fractionDigits);
  return fraction === "" && time.getUTCSeconds() === 0
    ? format.format(time, { time: "minutes" })
    : format.formatFractional(time, fraction);
}

/** The month that starts at the wall-clock time `wallMs`, its first day's
 *  midnight, as a board writes it with no zone: "January 2026" in the
 *  browser's language, or "2026-01" in the ISO format. */
export function boardMonthText(wallMs: number): string {
  const format = wallClockFormat();
  return format.preference.datePreset === "iso"
    ? dateText(new Date(wallMs), format).slice(0, -3)
    : MONTH_AND_YEAR.format(new Date(wallMs));
}

/** The whole days in `timeZone` from the one starting at wall-clock time
 *  `firstWallMs` to the one starting at `lastWallMs`, both included, as a board
 *  writes them: "May 11, 2026 to May 17, 2026 (UTC)", or "May 11, 2026 (UTC)"
 *  for one day. */
export function boardDaysText(
  firstWallMs: number,
  lastWallMs: number,
  timeZone: string,
): string {
  const first = boardWallClockText(firstWallMs);
  const last = boardWallClockText(lastWallMs);
  const days = first === last ? first : `${first} to ${last}`;
  return `${days} ${boardZoneText(timeZone)}`;
}

/**
 * A tick on a board's time axis at the wall-clock time `wallMs`, labeled as
 * Vega labels a time axis, by the finest part of the calendar that is not at
 * its start, and written in the viewer's format. A time of day has the
 * viewer's clock ("2:00 PM", "2:00:30 PM", "2:00:30.250 PM"), a day is its
 * date ("January 4, 2026"), a month its name ("January") and a year its
 * number ("2026"). The ISO format writes a day as YYYY-MM-DD and a month as
 * YYYY-MM: "2026-01-04", "2026-01". The label has no zone's name; the axis's
 * title names the zone once for every tick.
 */
export function boardTickText(wallMs: number): string {
  const tick = new Date(wallMs);
  const format = wallClockFormat();
  if (wallMs % MS_PER_DAY !== 0) {
    return format.formatParts(tick, { time: finestTimePart(tick) }).time;
  }
  if (tick.getUTCDate() !== 1) {
    return dateText(tick, format);
  }
  if (tick.getUTCMonth() === 0) {
    return String(tick.getUTCFullYear());
  }
  return format.preference.datePreset === "iso"
    ? boardMonthText(wallMs)
    : MONTH_NAME.format(tick);
}

/** The zone's name as a board writes it after a time: "(UTC)",
 *  "(Pacific Time)". */
export function boardZoneText(timeZone: string): string {
  return `(${timeZoneName(timeZone)})`;
}

/** The fewest digits below the second that write `belowSecond` nanoseconds
 *  exactly: none for zero, three for 0.123 s, up to nine. */
export function fewestFractionDigits(belowSecond: bigint): FractionDigits {
  return (
    belowSecond === 0n
      ? 0
      : belowSecond.toString().padStart(9, "0").replace(/0+$/, "").length
  ) as FractionDigits;
}

/** A month's name in the browser's language, for a tick at its first day. */
const MONTH_NAME = new Intl.DateTimeFormat(undefined, {
  month: "long",
  timeZone: "UTC",
});

/** A month's name and year in the browser's language. */
const MONTH_AND_YEAR = new Intl.DateTimeFormat(undefined, {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

/** The date of the wall-clock time `time`, written by `format` (a
 *  `wallClockFormat()`). */
function dateText(time: Date, format: ResolvedDateTimeFormat): string {
  return format.format(time, { time: "none" });
}

/** The digits below the second of the wall-clock time `time` plus
 *  `nsBelowMs` nanoseconds: `fractionDigits` of them, or the fewest that
 *  write it exactly; "" for none. */
function fractionText(
  time: Date,
  nsBelowMs: bigint,
  fractionDigits?: FractionDigits,
): string {
  const belowSecond = BigInt(time.getUTCMilliseconds()) * NS_PER_MS + nsBelowMs;
  return belowSecond
    .toString()
    .padStart(9, "0")
    .slice(0, fractionDigits ?? fewestFractionDigits(belowSecond));
}

/** The finest part of a time of day that is not zero at `tick`: its
 *  milliseconds, its seconds, or else its minutes. */
function finestTimePart(tick: Date): TimePrecision {
  if (tick.getUTCMilliseconds() !== 0) {
    return "millis";
  }
  return tick.getUTCSeconds() !== 0 ? "seconds" : "minutes";
}
